import {
  format,
  formatDistanceToNowStrict,
  formatRelative,
  differenceInDays,
  sub,
  type Locale,
  type FormatRelativeToken,
} from 'date-fns'
import { de, enGB, frCH, it } from 'date-fns/locale'

export default function useDate() {
  const languageStore = useLanguageStore()

  const relativeDateDefaults = {
    lastWeek: 'PP',
    nextWeek: 'PP',
    other: 'PP',
  }

  const localeData = computed<{ config: Locale; format: string }>(() => {
    switch (languageStore.locale) {
      case 'de-ch':
        return {
          config: {
            ...de,
            formatRelative(token: FormatRelativeToken) {
              return {
                ...relativeDateDefaults,
                yesterday: "'Gestern'",
                tomorrow: "'Morgen'",
                today: "'Heute'",
              }[token]
            },
          },
          format: 'd. LLLL yyyy',
        }
      case 'fr-ch':
        return {
          config: {
            ...frCH,
            formatRelative(token: FormatRelativeToken) {
              return {
                ...relativeDateDefaults,
                yesterday: "'Hier'",
                tomorrow: "'Demain'",
                today: "'Aujourd’hui'",
              }[token]
            },
          },
          format: 'd LLLL yyyy',
        }
      case 'it':
        return {
          config: {
            ...it,
            formatRelative(token: FormatRelativeToken) {
              return {
                ...relativeDateDefaults,
                yesterday: "'Ieri'",
                tomorrow: "'Domani'",
                today: "'Oggi'",
              }[token]
            },
          },
          format: 'd LLLL yyyy',
        }
      case 'en-gb':
      default:
        return {
          config: {
            ...enGB,
            formatRelative(token: FormatRelativeToken) {
              return {
                ...relativeDateDefaults,
                yesterday: "'Yesterday'",
                tomorrow: "'Tomorrow'",
                today: "'Today'",
              }[token]
            },
          },
          format: 'd LLLL yyyy',
        }
    }
  })

  const formatDate = (date: Date, pattern?: string) => {
    return format(date, pattern ?? localeData.value.format, { locale: localeData.value.config })
  }

  const formatRelativeDate = (date: Date) => {
    return formatRelative(date, new Date(), { locale: localeData.value.config })
  }

  const formatDistanceToNow = (date: Date) => {
    return formatDistanceToNowStrict(date, {
      roundingMethod: 'floor',
      locale: localeData.value.config,
    })
  }

  const getDifferenceInDays = (date1: Date, date2: Date) => {
    return differenceInDays(date1, date2)
  }

  const getDaysAgo = (now: Date, daysAgo: number): Date => {
    return sub(now, {
      days: daysAgo,
    })
  }

  const getShortMonthName = (month: number): string => {
    const date = new Date(0, month - 1)
    return format(date, 'MMM', { locale: localeData.value.config })
  }

  const getFormattedDateForVisualSignature = (date: Date): string => {
    const dateFormat = languageStore.locale === 'de-ch' ? 'd. LLL. yyyy' : 'd LLL yyyy'
    return formatDate(date, dateFormat)
  }

  /**
   * Returns the current timezone in IANA format (e.g., "Europe/Zurich").
   */
  const getCurrentTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    } catch (error) {
      console.error('Error fetching timezone:', error)
      return 'Europe/Zurich' // Fallback to Europe/Zurich if timezone cannot be determined
    }
  }

  return {
    formatDate,
    formatRelativeDate,
    formatDistanceToNow,
    getDifferenceInDays,
    getDaysAgo,
    getShortMonthName,
    getFormattedDateForVisualSignature,
    getCurrentTimezone,
  }
}
